import React, { useContext } from 'react'
import { RootContext as context } from '~/state/RootContext'

interface GraphEdgeProps {
  edge: {
    from: string
    to: string
  }
}

export const GraphEdge = ({ edge }: GraphEdgeProps): JSX.Element | null => {
  const { graph, layout } = useContext(context)

  const z = layout?.zoom ?? 1
  const weight = `${2 * z}px`
  const dist = 5 * z

  const from = graph?.nodes?.find((node) => node.key === edge.from)
  const to = graph?.nodes?.find((node) => node.key === edge.to)

  if (!from || !to) {
    console.log('No connection')
    return null
  }

  const fromElement = from.ref.current
  const toElement = to.ref.current
  const rootElement = layout?.root?.current

  if (!fromElement || !toElement || !rootElement) {
    console.log(`Missing element. ${fromElement} / ${toElement} / ${rootElement}`)
    return null
  }

  const { x: tx, y: ty } = rootElement.getBoundingClientRect()

  const isFaded =
    (graph?.highlight.length ?? 0) > 0 && edge.from !== graph?.highlight[0] && edge.to !== graph?.highlight[0]

  const stroke = isFaded ? '#dadada' : '#333'

  const { x: ax, y: ay, width: wa, height: ha } = fromElement.getBoundingClientRect()
  const { x: bx, y: by, height: hb } = toElement.getBoundingClientRect()

  const isHorizontal = Math.abs(by + hb / 2 - (ay + ha / 2)) < 2

  const offset = 5

  const left = ax + wa + offset - tx
  const top = Math.min(ay, by) + (isHorizontal ? Math.max(ha, hb) / 2 - 1 : 0) - ty
  const width = bx - left - offset - tx
  const height = isHorizontal ? 2 : ay < by ? by + hb - ay : Math.abs(ay + ha - by)

  const x1 = 0
  const y1 = height <= 2 ? 1 : ay < by ? ha / 2 : height - ha / 2

  const x2 = width
  const y2 = height <= 2 ? 1 : ay < by ? height - hb / 2 : hb / 2

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: 'absolute',
        left: left,
        top: top,
        overflow: 'visible',
        zIndex: isFaded ? 0 : 1,
      }}
      viewBox={`0 0 ${width} ${height}`}
    >
      <path
        d={`M ${x1} ${y1} C ${x1 + 0.75 * width} ${y1} ${x2 - 0.75 * width} ${y2} ${x2} ${y2}`}
        stroke={stroke}
        strokeWidth={weight}
        fill="none"
        style={{ transition: 'stroke', transitionDuration: '300ms' }}
      />
      <polyline
        points={`${x2 - dist},${y2 - dist} ${x2},${y2} ${x2 - dist},${y2 + dist}`}
        stroke={stroke}
        strokeWidth={weight}
        fill="none"
        style={{ transition: 'stroke', transitionDuration: '300ms' }}
      />
    </svg>
  )
}
